import type { RouteName } from '@/lib/routes';
import { ROADMAP, roadmapItem, shippedLate, shipVerdict, type RoadmapId } from '@/lib/roadmap';

/**
 * The release feed. Reverse-chronological; every entry states the one-line
 * fact and its date — feature depth stays owned by the area's topic page
 * (duplication guard, docs/page-briefs/README.md).
 *
 * ⚠ DAY-LEVEL DATES ARE PLACEHOLDERS pending owner confirmation (page brief
 * §8.3). The months are anchored to what the site already states publicly —
 * the docs changelog stub (components/docs/data/leaves.ts 'sup-changelog'),
 * SITE-PLAN §6's email-forwarding month, and the compare-page verify dates.
 * Replace the days with real ship dates (or git/deploy reconstruction)
 * whenever the owner confirms; never invent new month-level claims here.
 *
 * Entries with a `promise` reference lib/roadmap.ts by id — the "promise
 * kept" sash derives both of its dates from there, so a promise made in an
 * owner-page sash and a promise kept here are the same fact, shown twice.
 */
export type EntryType = 'feature' | 'improvement' | 'fix';

export interface ChangelogEntry {
  /** ISO date; also the stable anchor (#e-YYYY-MM-DD) and RSS guid. */
  iso: string;
  title: string;
  type: EntryType;
  area: string;
  areaRoute: RouteName;
  body: string;
  /** Set when this entry retires a roadmap sash — renders the sash-retirement strip. */
  promise?: RoadmapId;
}

export const TYPE_META: Record<EntryType, { label: string; color: string }> = {
  feature: { label: 'FEATURE', color: 'var(--color-data-green)' },
  improvement: { label: 'IMPROVEMENT', color: 'var(--color-data-blue)' },
  fix: { label: 'FIX', color: 'var(--color-data-amber)' },
};

export const ENTRIES: ChangelogEntry[] = [
  {
    iso: '2026-09-28',
    title: 'Self-serve export: links and click history',
    type: 'feature',
    area: 'Analytics',
    areaRoute: 'analytics',
    promise: 'takeout',
    body: 'Workspace owners and admins can download every link (JSON, CSV, or nginx and Caddy rule files) and the per-click request log (CSV or JSON) under Settings → Export; the analytics page downloads the clicks behind its current filters as CSV. The API gets the same over GET /v1/export/links and /v1/export/request-logs.',
  },
  {
    iso: '2026-08-12',
    title: 'The MCP server is live — agents can mint and route links',
    type: 'feature',
    area: 'AI / MCP',
    areaRoute: 'aiMcp',
    promise: 'mcp',
    body: 'Connect your AI assistant over MCP with OAuth — create links, attach routing rules and read analytics back from Claude, Cursor or any MCP client. Remote-only at mcp.rii.link, documented in the docs.',
  },
  {
    iso: '2026-08-05',
    title: 'Email forwarding is live',
    type: 'feature',
    area: 'Custom Domains',
    areaRoute: 'customDomains',
    promise: 'email-forwarding',
    body: 'Receive mail on your link domain — hello@yourbrand.com forwards straight to the inbox you already use, no mail server required. Set it up under Email in the dashboard, on a domain you have connected.',
  },
  {
    iso: '2026-08-03',
    title: 'QR codes render as SVG',
    type: 'improvement',
    area: 'QR Codes',
    areaRoute: 'qrCodes',
    body: 'POST /v1/qr now takes format: "svg" — vector output that scales from a business card to a billboard without re-rasterizing. PNG stays the default; branded styling applies to both formats.',
  },
  {
    iso: '2026-07-15',
    title: 'New routing condition: time of day',
    type: 'improvement',
    area: 'Smart Routing',
    areaRoute: 'smartRouting',
    body: 'Rules can now branch on the time of day in a timezone you choose — send a lunch-special link to the menu during opening hours and to the reservation page after close. Combines with geo, device and language conditions.',
  },
  {
    iso: '2026-06-24',
    title: 'Live click stream over SSE',
    type: 'feature',
    area: 'Developers / API',
    areaRoute: 'developers',
    body: 'GET /v1/events/stream pushes every click on your API-created links as a server-sent event — one curl -N away from a live dashboard. Outbound webhooks are still on the roadmap.',
  },
  {
    iso: '2026-06-18',
    title: 'Dynamic QR codes: edit the destination after printing',
    type: 'feature',
    area: 'QR Codes',
    areaRoute: 'qrCodes',
    body: 'A QR code printed on packaging used to be frozen at print time. Dynamic codes point at a short link instead of a destination — repoint the link and every already-printed code updates, no reprint.',
  },
  {
    iso: '2026-05-20',
    title: 'Dynamic responses: JSON / HTML / XML bodies from a link',
    type: 'feature',
    area: 'Dynamic Responses',
    areaRoute: 'dynamicResponses',
    body: 'A link can now answer with a body you write — JSON, HTML, XML or text, with the status code you choose — instead of redirecting. Route by query parameter or device to serve apps and browsers from one link. On Premium.',
  },
  {
    iso: '2026-03-10',
    title: 'Weekly time windows and traffic splits',
    type: 'improvement',
    area: 'Smart Routing',
    areaRoute: 'smartRouting',
    body: 'Routing rules accept weekly time windows — weekend or office-hour links fall back to a default outside them automatically — and percentage-based traffic splits for A/B tests.',
  },
  {
    iso: '2026-02-02',
    title: 'REST API + API keys — general availability',
    type: 'feature',
    area: 'Developers / API',
    areaRoute: 'developers',
    body: 'The public REST API is open: create links with routing rules, connect domains, make QR codes and read per-link stats — with an API key.',
  },
];

/** Areas in feed order (for the filter select), deduped. */
export const AREAS: string[] = [...new Set(ENTRIES.map((e) => e.area))];

export interface ChangelogStats {
  /** Entries in the latest year of the feed. */
  releases: number;
  releasesYear: number;
  /** Promise-kept entries (a roadmap sash retired by a shipped entry). */
  kept: number;
  /** Roadmap items shipped past their promised window — the honesty counter. */
  late: number;
}

/**
 * Derived, never hand-typed (brief §4.1): the hero chips are computed from
 * the data so "0 SHIPPED LATE" stops being claimable the day it stops being
 * true, automatically.
 */
export function changelogStats(): ChangelogStats {
  const releasesYear = Number(ENTRIES[0].iso.slice(0, 4));
  return {
    releasesYear,
    releases: ENTRIES.filter((e) => e.iso.startsWith(String(releasesYear))).length,
    kept: ENTRIES.filter((e) => e.promise).length,
    late: ROADMAP.filter(shippedLate).length,
  };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-08-05' → 'Aug 5, 2026' */
export function formatIso(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${d}, ${y}`;
}

/** Sash strip data for a promise-kept entry, both dates from lib/roadmap. */
export function sashFor(entry: ChangelogEntry) {
  if (!entry.promise) return null;
  const item = roadmapItem(entry.promise);
  const NOTE = { early: 'SHIPPED EARLY', 'on-time': 'ON TIME', late: 'SHIPPED LATE' } as const;
  const verdict = shipVerdict(item);
  return {
    promisedLabel: `Promised · ${item.promised}`,
    shippedLabel: `Shipped · ${formatIso(item.shipped ?? entry.iso)}`,
    note: verdict ? NOTE[verdict] : '',
    late: verdict === 'late',
  };
}
